/**
 * #4576 SHARENARROW: what a person reads when GET /api/sharing/:token
 * withholds a shared Analyst conversation. lib/shared-tool-grants.ts decides;
 * this says which stored result decided it and why, in place of a bare 403.
 *
 * The owner and a viewer get the same sentence for the same decision, so an
 * owner can tell why their own link stopped opening. The sentence names the
 * tool and the reason only: never the result's rows, a product it names, or
 * the conversation's title.
 */
import { refusedPageLabel } from "./page-reachability";

/** The 403 body's `error` and `code`; the client keys on `code`. */
export const SHARE_WITHHELD_ERROR = "Result withheld";
export const SHARE_WITHHELD_CODE = "share_result_withheld";

/**
 * #4576: the Finished Goods page, where every QA product's QC results show,
 * static or QC-minted, and the API its reads go through. A QC-minted product's
 * stored count_tests or get_recent_failures result opens for a viewer who
 * holds both, resolved as rbacMiddleware and the route guard resolve them.
 */
export const QC_PAGE_GRANTS = { page: "/quality", api: "/api/quality" } as const;

/** Why a stored result is withheld from this reader. */
export type WithheldWhy =
  /** The rows are the owner's own, or no reader's access but the owner's can be proven for them. */
  | "owner_only"
  /** The reader's role does not hold the tool. */
  | "tool"
  /** A QC-minted product's result, and the reader does not hold the Finished Goods page. */
  | "qc_page"
  /** The reader's grants (page, API, capability, tables, space, membership) do not cover the result. */
  | "access"
  /** The stored record cannot be read, so it cannot be checked for anyone. */
  | "unreadable";

export interface ShareWithheld {
  /** The tool whose stored result decided, or null when no record could name one. */
  tool: string | null;
  why: WithheldWhy;
  /** "memory": the result sat behind a saved memory an answer drew on, not a call in this conversation. */
  via: "conversation" | "memory";
}

export interface ShareWithheldBody {
  error: typeof SHARE_WITHHELD_ERROR;
  code: typeof SHARE_WITHHELD_CODE;
  message: string;
  withheld: ShareWithheld;
}

const article = (word: string) => (/^[aeiou]/i.test(word) ? "an" : "a");

/** The one sentence a reader sees for this decision. */
export function shareWithheldMessage({ tool, why, via }: ShareWithheld): string {
  const result = tool ? `${article(tool)} ${tool} result` : null;
  const subject =
    via === "memory"
      ? `An answer in this conversation drew on a saved memory${result ? ` from ${result}` : ""}`
      : `This conversation includes ${result ?? "a tool result"}`;
  switch (why) {
    case "qc_page": {
      const page = refusedPageLabel(QC_PAGE_GRANTS.page);
      return page
        ? `${subject} for a product on ${page}. You do not have access to ${page}.`
        : `${subject} for a QC product. You do not have access to that product's page.`;
    }
    case "access":
      return `${subject} that your access does not cover.`;
    case "tool":
      return `${subject}. Your role does not have that tool.`;
    case "owner_only":
      return `${subject}. Only the conversation's owner can open it.`;
    case "unreadable":
      return `${subject} that could not be read, so it cannot be checked.`;
    default: {
      const never: never = why;
      void never;
      return `${subject} that your access does not cover.`;
    }
  }
}

/** A catalogue tool name's shape. A stored record's tool is free text, so anything else is named as "a tool result". */
const TOOL_NAME_SHAPE = /^[a-z][a-z0-9_]{0,63}$/;

export function shareWithheldBody(withheld: ShareWithheld): ShareWithheldBody {
  const named = { ...withheld, tool: withheld.tool !== null && TOOL_NAME_SHAPE.test(withheld.tool) ? withheld.tool : null };
  return { error: SHARE_WITHHELD_ERROR, code: SHARE_WITHHELD_CODE, message: shareWithheldMessage(named), withheld: named };
}
